import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, User, Mail } from "lucide-react";

const TestResultsModal = ({
  isOpen,
  onClose,
  userInfo,
  fetchedUserResponses = [],
  loading = false,
  error = null,
}) => {
  if (!isOpen || !userInfo) return null;

  const { name, surname, email, userResponses, sessionLink } = userInfo;
  const displayedResponses =
    fetchedUserResponses.length > 0 ? fetchedUserResponses : userResponses || [];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] rounded-[20px] md:rounded-[33px] w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl notranslate"
            translate="no"
          >
            {/* Header */}
            <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] p-6 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-black hover:bg-opacity-10 rounded-full transition-colors"
              >
                <X size={24} className="text-black" />
              </button>
              <div className="pr-12">
                <div className="flex items-center mb-3">
                  <User size={24} className="text-black mr-2" />
                  <h2 className="text-2xl font-poppins font-bold text-black">
                    {name} {surname}
                  </h2>
                </div>
                <div className="flex items-center">
                  <Mail size={16} className="text-black mr-2" />
                  <p className="text-black font-poppins text-sm opacity-70">{email}</p>
                </div>
                {sessionLink && (
                  <div className="mt-3">
                    <a
                      href={sessionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 font-poppins text-sm underline hover:text-blue-700 transition-colors"
                    >
                      Click here to join the session
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loading ? (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] rounded-[20px] md:rounded-[33px] p-8 shadow-lg">
                    <h3 className="text-xl font-poppins font-semibold text-black mb-2">
                      Client responses are loading...
                    </h3>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] rounded-[20px] md:rounded-[33px] p-8 shadow-lg">
                    <h3 className="text-xl font-poppins font-semibold text-black mb-2">
                      Failed to retrieve client responses
                    </h3>
                    <p className="text-black font-poppins opacity-70">{error}</p>
                  </div>
                </div>
              ) : displayedResponses.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#F4F6FF] rounded-[15px] md:rounded-[25px] p-4 shadow-md border border-gray-200">
                    <h3 className="text-lg font-poppins font-semibold text-black">
                      Client Responses Before Session
                    </h3>
                    <p className="text-sm text-black opacity-70 font-poppins">
                      The client's test and question responses are listed below.
                    </p>
                  </div>
                  {displayedResponses.map((response, index) => (
                    <motion.div
                      key={response._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      >
                      <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] rounded-[15px] md:rounded-[25px] p-6 shadow-lg">
                      {/* Test Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-poppins font-semibold text-black">
                            {response.testId?.name || "Test"}
                          </h3>
                          <p className="text-black font-poppins text-sm mt-1 opacity-70">
                            {response.testId?.description || "No description available"}
                          </p>
                        </div>
                      </div>

                      <hr className="border-black border-[1px] mb-4 opacity-20" />

                      {/* Questions and Answers */}
                      <div>
                        <h4 className="text-lg font-poppins font-semibold text-black mb-4 flex items-center">
                          <FileText size={18} className="mr-2" />
                          Questions and Answers
                        </h4>
                        <div className="space-y-3">
                          {response.responses?.map((resp, respIndex) => (
                            <div
                              key={resp._id}
                              className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#F0F0F0] rounded-[10px] md:rounded-[15px] p-4 shadow-md border border-gray-200"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="text-sm text-black font-poppins opacity-70 mb-2">
                                    Question {respIndex + 1}
                                  </div>
                                  
                                  {/* Question Text */}
                                  <div className="bg-gradient-to-b from-[#F8F8F8] to-[#EEEEEE] rounded-[8px] md:rounded-[12px] p-3 mb-3 shadow-sm">
                                    <span className="text-sm font-poppins font-medium text-black">
                                      Question:
                                    </span>
                                    <p className="text-black font-poppins mt-1">
                                      {(resp.questionText || resp.questionId?.questionText || resp.questionId?.text || resp.questionId?.title || "Question text not found")
                                        .replace(/^__EMERGENCY_CONTACT__\n?/, "")}
                                    </p>
                                  </div>
                                  
                                  {/* Answer */}
                                  <div className="bg-gradient-to-b from-[#F0F0F0] to-[#E8E8E8] rounded-[8px] md:rounded-[12px] p-3 shadow-sm">
                                    <span className="text-sm font-poppins font-medium text-black">
                                      Answer:
                                    </span>
                                    {(() => {
                                      try {
                                        const parsed = JSON.parse(resp.answer);
                                        if (parsed && typeof parsed === "object" && ("fullName" in parsed || "relationship" in parsed || "phoneNumber" in parsed)) {
                                          return (
                                            <div className="mt-2 space-y-1">
                                              <p className="text-black font-poppins text-sm"><span className="font-semibold">Full Name:</span> {parsed.fullName || "-"}</p>
                                              <p className="text-black font-poppins text-sm"><span className="font-semibold">Relationship:</span> {parsed.relationship || "-"}</p>
                                              <p className="text-black font-poppins text-sm"><span className="font-semibold">Phone Number:</span> {parsed.phoneNumber || "-"}</p>
                                              <p className="text-black font-poppins text-sm">
                                                <span className="font-semibold">Confirmed:</span>{" "}
                                                <span className={parsed.confirmed ? "text-green-600" : "text-red-500"}>
                                                  {parsed.confirmed ? "Yes" : "No"}
                                                </span>
                                              </p>
                                            </div>
                                          );
                                        }
                                      } catch {}
                                      return (
                                        <span className="text-black font-poppins font-semibold ml-2">
                                          {resp.answer}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Therapist Notes */}
                      {response.therapistNotes && (
                        <div className="mt-6 bg-gradient-to-b from-[#FFF8E1] to-[#F5F5F5] rounded-[10px] md:rounded-[15px] p-4 shadow-md border border-gray-200">
                          <h5 className="font-poppins font-semibold text-black mb-2">
                            Therapist Notes:
                          </h5>
                          <p className="text-black font-poppins">{response.therapistNotes}</p>
                        </div>
                      )}

                      {/* Result */}
                      {response.result && (
                        <div className="mt-6 bg-gradient-to-b from-[#F0F8FF] to-[#E8E8E8] rounded-[10px] md:rounded-[15px] p-4 shadow-md border border-gray-200">
                          <h5 className="font-poppins font-semibold text-black mb-2">
                            Test Result:
                          </h5>
                          <p className="text-black font-poppins">{response.result}</p>
                        </div>
                      )}
                    </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] rounded-[20px] md:rounded-[33px] p-8 shadow-lg">
                    <FileText size={64} className="mx-auto text-black opacity-30 mb-4" />
                    <h3 className="text-xl font-poppins font-semibold text-black mb-2">
                      Danisan cevabi bulunamadi
                    </h3>
                    <p className="text-black font-poppins opacity-70">
                      Bu danisan henuz soru veya test cevabi gondermemis.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gradient-to-b from-[#FFFFFF] via-[#FFFFFF] to-[#E5D3DD] p-4 text-center">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gradient-to-b from-[#FFFFFF] to-[#E5D3DD] text-black font-poppins font-medium rounded-full hover:shadow-lg transition-all duration-200 shadow-md border border-gray-300"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TestResultsModal;
